import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { giftCardsApi } from '../api/giftCards'
import EstadoBadgeGiftCard from './EstadoBadgeGiftCard'
import Modal from './Modal'
import Boton from './Boton'
import MoneyInput from './MoneyInput'
import ModalMandarGiftCard from './ModalMandarGiftCard'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { descargarArchivo } from '../utils/descargar'
import { colors, font, text, sx } from '../design/tokens'

const LABELS_CANJE = { reservado: 'Reservado', aplicado: 'Aplicado', liberado: 'Liberado' }
const COLORES_CANJE = {
  reservado: { bg: colors.warnBg, color: colors.warn },
  aplicado: { bg: colors.okBg, color: colors.ok },
  liberado: { bg: colors.bgSoft, color: colors.textSec },
}

/**
 * Card "🎁 Gift cards" del detalle de NV: las emitidas por esta venta (al
 * facturarse), los canjes aplicados sobre ella, y el botón para canjear una
 * mientras la NV está en borrador.
 */
export default function GiftCardsNVCard({ nv, onChange }) {
  const navigate = useNavigate()
  const [data, setData] = useState({ emitidas: [], canjes: [] })
  const [showCanje, setShowCanje] = useState(false)
  const [mandar, setMandar] = useState(null)        // card con el modal ✉️ abierto
  const [avisoMail, setAvisoMail] = useState('')

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await giftCardsApi.porNV(nv.id)
      setData(res)
    } catch { /* sin permisos o error transitorio: la card no molesta */ }
  }, [nv.id])

  useEffect(() => { cargar() }, [cargar, nv.estado, nv.items?.length])

  const puedeCanjear = nv.estado === 'borrador' && !nv.archivado
  const canjesVisibles = data.canjes.filter(c => c.estado !== 'liberado')
  // Se pueden cargar VARIAS gift cards en la misma venta (cada una reserva su
  // parte; la suma no puede pasar el total). El botón lo dice para que el
  // vendedor no crea que con una ya está. Mientras quede algo por cubrir.
  const hayReservas = canjesVisibles.some(c => c.estado === 'reservado')
  const restaCubrir = parseFloat(nv.total) || 0

  // Gift cards que esta NV va a EMITIR al facturar (líneas GIF positivas).
  // Mientras no exista la card, el vendedor registra acá los datos del regalo
  // (sug #48): destinatario, mail, dedicatoria, fecha de envío.
  const unidadesAEmitir = (nv.items || [])
    .filter(it => (it.articulo?.codigo_alfa || '').toUpperCase().startsWith('GIF') && parseFloat(it.precio_final) > 0)
    .reduce((acc, it) => acc + Math.max(1, Math.round(parseFloat(it.cantidad) || 1)), 0)
  // Hasta que se emitan las cards (al facturar) — no solo en borrador: en las
  // ventas corporativas la factura sale antes de saber a quién va cada card
  // (sug #87). Emitidas, el mail se carga en cada una con ✉️ Mandar.
  const mostrarMeta = !nv.archivado && unidadesAEmitir > 0 && data.emitidas.length === 0

  if (!puedeCanjear && !mostrarMeta && data.emitidas.length === 0 && canjesVisibles.length === 0) return null

  const quitarCanje = async (canje) => {
    if (!window.confirm(`¿Quitar el canje de $ ${fmtMonto(canje.monto)} (${canje.gift_card?.codigo})? La línea negativa se elimina de la NV.`)) return
    await giftCardsApi.quitarCanje(canje.id)
    await cargar()
    onChange?.()
  }

  return (
    <div style={{ ...sx.card, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: text.title }}>🎁 Gift cards</h3>
        {puedeCanjear && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {hayReservas && (
              <span style={{ fontSize: text.meta, color: restaCubrir > 0 ? colors.textMeta : colors.ok }}>
                {restaCubrir > 0 ? `Resta cubrir $ ${fmtMonto(restaCubrir)}` : 'Venta cubierta al 100%'}
              </span>
            )}
            {(!hayReservas || restaCubrir > 0) && (
              <Boton variante="secondary" sm onClick={() => setShowCanje(true)}>
                {hayReservas ? 'Canjear otra gift card' : 'Canjear gift card'}
              </Boton>
            )}
          </div>
        )}
      </div>

      {data.emitidas.length === 0 && canjesVisibles.length === 0 && !mostrarMeta && (
        <p style={{ fontSize: text.sec, color: colors.textMeta, margin: 0 }}>
          Sin gift cards en esta NV. Si la NV vende una (artículo GIF2500), se emite sola al facturar;
          para pagar con una, usá «Canjear gift card».
        </p>
      )}

      {mostrarMeta && (
        <GiftMetaForm nv={nv} unidades={unidadesAEmitir} />
      )}

      {data.emitidas.length > 0 && (
        <div style={{ marginBottom: canjesVisibles.length ? 12 : 0 }}>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Emitidas por esta venta
          </div>
          {data.emitidas.map(g => (
            <div key={g.id} style={{ padding: '6px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: text.body }}>
                <a style={{ ...sx.mono, fontWeight: 600, color: colors.link, cursor: 'pointer' }} onClick={() => navigate(`/gift-cards/${g.id}`)}>
                  {g.codigo}
                </a>
                <EstadoBadgeGiftCard estado={g.estado} />
                <span style={sx.mono}>$ {fmtMonto(g.monto_inicial)}</span>
                {g.vence_el && <span style={{ color: colors.textSec, fontSize: text.sec }}>vence {fmtFecha(g.vence_el)}</span>}
                {['activa', 'pendiente'].includes(g.estado) && (
                  <Boton variante="secondary" sm style={{ marginLeft: 'auto' }}
                    title="Cargar o corregir el mail del destinatario y mandarla (o reenviarla si ya salió)"
                    onClick={() => { setAvisoMail(''); setMandar(g) }}>
                    {g.mail_enviado_at ? '✉️ Reenviar' : '✉️ Mandar'}
                  </Boton>
                )}
                <Boton variante="secondary" sm style={['activa', 'pendiente'].includes(g.estado) ? {} : { marginLeft: 'auto' }} onClick={() => descargarArchivo(`/gift-cards/${g.id}/pdf`, `gift_card_${g.codigo}.pdf`)}>
                  ⬇ PDF
                </Boton>
              </div>
              {/* Destinatario + estado del MAIL de regalo (sug #48). Se carga o
                  corrige con ✉️ Mandar (sug #87/#88). */}
              <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  Para: {g.beneficiario_nombre || '—'}
                  {g.beneficiario_email ? ` · ${g.beneficiario_email}` : ' · sin mail (se entrega impresa)'}
                </span>
                <span style={{ color: g.mail_enviado_at ? colors.ok : colors.textMeta }}>
                  {g.mail_enviado_at
                    ? `✉ enviada ${fmtFecha(g.mail_enviado_at)}`
                    : g.enviar_el
                      ? `✉ programada para ${fmtFecha(g.enviar_el)}`
                      : (g.beneficiario_email ? '✉ se envía al activarse (recibo + factura)' : '')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {avisoMail && <div style={{ fontSize: text.sec, color: colors.ok, margin: '6px 0' }}>✓ {avisoMail}</div>}
      {mandar && (
        <ModalMandarGiftCard card={mandar} onClose={() => setMandar(null)}
          onEnviada={async (msg) => { setAvisoMail(msg); await cargar() }} />
      )}

      {canjesVisibles.length > 0 && (
        <div>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Canjes en esta NV
          </div>
          {canjesVisibles.map(c => {
            const cc = COLORES_CANJE[c.estado] || COLORES_CANJE.liberado
            return (
              <div key={c.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0', fontSize: text.body, borderBottom: `1px solid ${colors.borderSoft}` }}>
                <a style={{ ...sx.mono, fontWeight: 600, color: colors.link, cursor: 'pointer' }} onClick={() => navigate(`/gift-cards/${c.gift_card_id}`)}>
                  {c.gift_card?.codigo}
                </a>
                <span style={{ ...sx.mono }}>− $ {fmtMonto(c.monto)}</span>
                <span style={{ display: 'inline-block', background: cc.bg, color: cc.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  {LABELS_CANJE[c.estado]}
                </span>
                {c.estado === 'reservado' && (
                  <span style={{ fontSize: text.meta, color: colors.textMeta }}>se descuenta al facturar</span>
                )}
                {c.estado === 'reservado' && puedeCanjear && (
                  <Boton variante="ghost" sm style={{ marginLeft: 'auto' }} onClick={() => quitarCanje(c)}>✕ Quitar</Boton>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showCanje && (
        <CanjearGiftCardModal
          nv={nv}
          onClose={() => setShowCanje(false)}
          onDone={async () => { setShowCanje(false); await cargar(); onChange?.() }}
        />
      )}
    </div>
  )
}

function CanjearGiftCardModal({ nv, onClose, onDone }) {
  const [codigo, setCodigo] = useState('')
  const [card, setCard] = useState(null)
  const [monto, setMonto] = useState('')
  const [error, setError] = useState(null)
  const [procesando, setProcesando] = useState(false)

  const inputSx = {
    width: '100%', padding: '8px 10px', border: `1px solid ${colors.textPlh}`,
    borderRadius: 6, fontSize: text.body, boxSizing: 'border-box',
  }

  const validar = async () => {
    setError(null)
    setCard(null)
    try {
      const { data } = await giftCardsApi.validar(codigo)
      setCard(data)
      if (!data.canjeable) {
        setError(`La gift card está ${data.estado} — no se puede canjear.`)
        return
      }
      const sugerido = Math.min(data.saldo_disponible, parseFloat(nv.total) || data.saldo_disponible)
      setMonto(String(sugerido > 0 ? sugerido : ''))
    } catch (e) {
      setError(e.response?.status === 404 ? 'No existe una gift card con ese código.' : 'No se pudo validar el código.')
    }
  }

  const confirmar = async () => {
    setError(null)
    setProcesando(true)
    try {
      await giftCardsApi.canjear({ codigo, nota_venta_id: nv.id, monto: parseFloat(monto) || 0 })
      await onDone()
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo canjear.'))
    } finally {
      setProcesando(false)
    }
  }

  return (
    <Modal title="Canjear gift card" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Código
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={codigo}
              onChange={e => { setCodigo(e.target.value); setCard(null); setError(null) }}
              onKeyDown={e => e.key === 'Enter' && codigo.trim() && validar()}
              placeholder="LGC-XXXX-XXXX"
              autoFocus
              style={{ ...inputSx, flex: 1, fontFamily: font.mono, letterSpacing: '0.08em' }}
            />
            <Boton variante="secondary" onClick={validar} disabled={!codigo.trim()}>Validar</Boton>
          </div>
        </div>

        {card && card.canjeable && (
          <>
            <div style={{ background: colors.okBg, color: colors.ok, padding: '10px 14px', borderRadius: 6, fontSize: text.sec }}>
              <strong style={sx.mono}>{card.codigo}</strong> · saldo disponible <strong>$ {fmtMonto(card.saldo_disponible)}</strong>
              {card.vence_el && <> · vence {fmtFecha(card.vence_el)}</>}
              {card.beneficiario_nombre && <> · {card.beneficiario_nombre}</>}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Monto a canjear
              </label>
              <MoneyInput value={monto} onChange={setMonto} style={inputSx} />
              <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 4 }}>
                Resta cubrir de la venta: <strong>$ {fmtMonto(parseFloat(nv.total) || 0)}</strong>. Se agrega como línea negativa GIF2500;
                el saldo se descuenta recién al facturar. Si no alcanza, después podés canjear otra gift card.
              </div>
            </div>
          </>
        )}

        {error && (
          <div style={{ background: colors.errBg, color: colors.err, padding: '8px 12px', borderRadius: 6, fontSize: text.sec }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton
            variante="primary"
            onClick={confirmar}
            disabled={procesando || !card?.canjeable || !(parseFloat(monto) > 0)}
          >
            {procesando ? 'Canjeando…' : 'Confirmar canje'}
          </Boton>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Datos del regalo de las gift cards que la NV va a emitir al facturar
 * (sug #48): destinatario, mail, de parte de, dedicatoria y fecha de envío.
 * Se guardan en la NV (nota_venta.gift_meta) y la emisión los usa; sin datos,
 * la card sale a nombre del cliente de la ficha.
 */
function GiftMetaForm({ nv, unidades }) {
  const VACIA = { para_nombre: '', para_email: '', de_nombre: '', dedicatoria: '', enviar_el: '' }
  const [tarjetas, setTarjetas] = useState(() => {
    const base = Array.isArray(nv.gift_meta) ? nv.gift_meta : []
    return Array.from({ length: Math.min(unidades, 10) }, (_, i) => ({ ...VACIA, ...(base[i] || {}) }))
  })
  const [guardando, setGuardando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [error, setError] = useState(null)

  const setCampo = (idx, campo, valor) => setTarjetas(prev =>
    prev.map((t, i) => i === idx ? { ...t, [campo]: valor } : t))

  const guardar = async () => {
    setGuardando(true); setMsg(null); setError(null)
    try {
      await giftCardsApi.guardarMetaNV(nv.id, tarjetas)
      setMsg('Datos del regalo guardados — se usan al emitir la gift card (al facturar).')
    } catch (err) {
      const data = err.response?.data
      const detalle = data?.errors ? Object.values(data.errors).flat().join(' · ') : null
      setError(detalle || data?.message || 'No se pudieron guardar los datos del regalo.')
    } finally { setGuardando(false) }
  }

  const inputSx = {
    width: '100%', padding: '6px 8px', border: `1px solid ${colors.borderSoft}`,
    borderRadius: 6, fontSize: text.sec, boxSizing: 'border-box',
  }
  const lblSx = { fontSize: text.meta, fontWeight: 600, color: colors.textSec, display: 'block', marginBottom: 2 }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Datos del regalo ({unidades === 1 ? '1 gift card a emitir' : `${unidades} gift cards a emitir`})
      </div>
      <p style={{ fontSize: text.sec, color: colors.textMeta, margin: '0 0 8px' }}>
        La card se emite al facturar y se habilita con el recibo y la factura; el mail sale en ese momento (o el día elegido).
        Sin estos datos, sale a nombre del cliente de la ficha.
      </p>
      {tarjetas.map((t, idx) => (
        <div key={idx} style={{ border: `1px solid ${colors.borderSoft}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
          {tarjetas.length > 1 && (
            <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, marginBottom: 6 }}>Gift card {idx + 1}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lblSx}>Para (nombre)</label>
              <input style={inputSx} value={t.para_nombre} onChange={e => setCampo(idx, 'para_nombre', e.target.value)} placeholder="Destinatario del regalo" />
            </div>
            <div>
              <label style={lblSx}>Mail del destinatario</label>
              <input style={inputSx} type="email" value={t.para_email} onChange={e => setCampo(idx, 'para_email', e.target.value)} placeholder="a-donde-mandarla@mail.com" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lblSx}>De parte de</label>
              <input style={inputSx} value={t.de_nombre} onChange={e => setCampo(idx, 'de_nombre', e.target.value)} placeholder="Quien regala" />
            </div>
            <div>
              <label style={lblSx}>Mandar el mail el… (opcional)</label>
              <input style={inputSx} type="date" value={t.enviar_el ? String(t.enviar_el).slice(0, 10) : ''} onChange={e => setCampo(idx, 'enviar_el', e.target.value)} />
            </div>
          </div>
          <div>
            <label style={lblSx}>Dedicatoria (opcional)</label>
            <textarea style={{ ...inputSx, minHeight: 44, resize: 'vertical' }} data-nocaps value={t.dedicatoria}
              onChange={e => setCampo(idx, 'dedicatoria', e.target.value)} maxLength={2000} placeholder="Mensaje personal que va en el mail" />
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Boton variante="secondary" sm disabled={guardando} onClick={guardar}>
          {guardando ? 'Guardando…' : '💾 Guardar datos del regalo'}
        </Boton>
        {msg && <span style={{ fontSize: text.sec, color: colors.ok }}>{msg}</span>}
        {error && <span style={{ fontSize: text.sec, color: colors.err }}>{error}</span>}
      </div>
    </div>
  )
}
